/**
 * Las tareas programadas del asistente (D65 de `conversaciones-agente-ia`): «@asistente el martes
 * 20hs mandale que ya tenemos stock». La lógica sin React: la fecha en hora de Argentina, el estado,
 * la franja de la conversación y qué tarjeta va en qué nota.
 *
 * El conversation-service avisa con `agent:tasks` `{conversationId}` cada vez que una tarea de la
 * conversación cambia —se crea, se cancela, cambia de hora, empieza o termina— y el front relee
 * `GET /conversations/:id/agent-tasks`. Nada de esto lleva texto: una tarea son ids y una hora.
 */
import type {
  AgentTaskStatus,
  NoteTask,
  TaskView,
  TimelineRow,
} from "@/services/conversations.api";

const MINUTO = 60_000;
/**
 * La hora de una tarea es la de Argentina, sin importar dónde esté el navegador. Argentina no tiene
 * horario de verano: -03:00 todo el año, el mismo offset que se le manda a la API.
 */
const OFFSET_ARGENTINA = "-03:00";
const OFFSET_ARGENTINA_MS = -3 * 60 * MINUTO;
/** Lo que valida el conversation-service: entre un minuto y 60 días desde ahora. */
export const TAREA_MAX_DIAS = 60;
/** Una pendiente cuya hora pasó hace más que esto está atrasada: el pod no la tomó a tiempo. */
const ATRASO_MS = 2 * MINUTO;

const DIAS = [
  "domingo",
  "lunes",
  "martes",
  "miércoles",
  "jueves",
  "viernes",
  "sábado",
];
const dos = (n: number) => String(n).padStart(2, "0");

/** El instante corrido a la hora de Argentina: se lee con los getters UTC. */
function enArgentina(instante: string | Date): Date | null {
  const t =
    typeof instante === "string" ? Date.parse(instante) : instante.getTime();
  return Number.isNaN(t) ? null : new Date(t + OFFSET_ARGENTINA_MS);
}

/**
 * «martes 18/11 · 20:00», en hora de Argentina: la fecha entera es la verificación de que el
 * asistente entendió bien «el martes». Con `ahora` de otro año, lleva el año: «… 18/11/2025 · …».
 */
export function fechaDeTarea(iso: string, ahora?: Date): string {
  const d = enArgentina(iso);
  if (!d) return "—";
  const hoy = ahora ? enArgentina(ahora) : null;
  const anio =
    hoy && hoy.getUTCFullYear() !== d.getUTCFullYear()
      ? `/${d.getUTCFullYear()}`
      : "";
  return (
    `${DIAS[d.getUTCDay()]} ${dos(d.getUTCDate())}/${dos(
      d.getUTCMonth() + 1,
    )}${anio}` + ` · ${dos(d.getUTCHours())}:${dos(d.getUTCMinutes())}`
  );
}

/** Día (`YYYY-MM-DD`) y hora (`HH:mm`) de Argentina por separado: lo que muestran los selectores. */
export function partesEnArgentina(
  instante: string | Date,
): { fecha: string; hora: string } | null {
  const d = enArgentina(instante);
  if (!d) return null;
  return {
    fecha: `${d.getUTCFullYear()}-${dos(d.getUTCMonth() + 1)}-${dos(
      d.getUTCDate(),
    )}`,
    hora: `${dos(d.getUTCHours())}:${dos(d.getUTCMinutes())}`,
  };
}

/** El día y la hora que eligió el operador, en hora de Argentina, como los pide la API. */
export function horaArgentina(fecha: string, hora: string): string {
  return `${fecha}T${hora}:00${OFFSET_ARGENTINA}`;
}

/** Por qué no se puede programar para esa hora, o `null` si se puede. Las mismas reglas que la API. */
export function errorDeHora(runAt: string, ahora: Date): string | null {
  const t = Date.parse(runAt);
  if (Number.isNaN(t)) return "Elegí el día y la hora";
  if (t < ahora.getTime() + MINUTO)
    return "Tiene que ser por lo menos un minuto después de ahora";
  if (t > ahora.getTime() + TAREA_MAX_DIAS * 24 * 60 * MINUTO)
    return `Puede ser hasta ${TAREA_MAX_DIAS} días desde ahora`;
  return null;
}

/**
 * El estado a mostrar. El servicio pasa una pendiente a `late` cuando se lo piden; entre una lectura
 * y otra, una pendiente cuya hora ya pasó se muestra atrasada sin esperar al próximo `agent:tasks`.
 */
export function estadoDeTarea(
  t: { status: AgentTaskStatus; runAt: string },
  ahora: Date,
): AgentTaskStatus {
  if (t.status !== "pending") return t.status;
  const run = Date.parse(t.runAt);
  return !Number.isNaN(run) && ahora.getTime() - run > ATRASO_MS
    ? "late"
    : "pending";
}

/** Texto y color de la etiqueta (los `color` de `Tag` de antd). */
export const ESTADOS_DE_TAREA: Record<
  AgentTaskStatus,
  { texto: string; color: string }
> = {
  pending: { texto: "Programada", color: "blue" },
  late: { texto: "Atrasada", color: "warning" },
  running: { texto: "Corriendo", color: "processing" },
  done: { texto: "Hecha", color: "success" },
  failed: { texto: "Falló", color: "error" },
  cancelled: { texto: "Cancelada", color: "default" },
};

/** «Programada», «Falló», «Cancelada por Juan Pérez». */
export function textoDelEstado(
  status: AgentTaskStatus,
  cancelledByName?: string | null,
): string {
  if (status === "cancelled" && cancelledByName?.trim())
    return `Cancelada por ${cancelledByName.trim()}`;
  return ESTADOS_DE_TAREA[status]?.texto ?? status;
}

/** Cancelar y cambiar la hora: sólo mientras no corrió. Quién puede lo decide la API. */
export function sePuedeCambiar(status: AgentTaskStatus): boolean {
  return status === "pending" || status === "late";
}

/**
 * Lo que se le dice al operador cuando la API no deja cancelar o cambiar la hora. `releer`: la
 * tarea en pantalla está vieja —ya corrió, la cancelaron, no existe— y hay que volver a pedirla.
 * `nivel`: un 403 o una tarea que ya no está pendiente no son errores, son la regla.
 */
export function errorDeTarea(
  e: unknown,
  accion: "cancelar" | "cambiar",
): { texto: string; nivel: "info" | "warning" | "error"; releer: boolean } {
  const r = (
    e as {
      response?: {
        status?: number;
        data?: { code?: string; message?: unknown };
      };
    }
  )?.response;
  const code = r?.data?.code;
  if (code === "TASK_CANCEL_DENIED" || (!code && r?.status === 403))
    return {
      texto:
        accion === "cancelar"
          ? "Sólo quien pidió la tarea o un Admin de Operadores del Store la puede cancelar"
          : "Sólo quien pidió la tarea o un Admin de Operadores del Store le puede cambiar la hora",
      nivel: "warning",
      releer: false,
    };
  if (code === "TASK_NOT_PENDING")
    return {
      texto:
        "La tarea ya no está pendiente: ya corrió, está corriendo o la cancelaron",
      nivel: "info",
      releer: true,
    };
  if (code === "TASK_NOT_FOUND")
    return { texto: "La tarea ya no existe", nivel: "info", releer: true };
  if (code === "TASK_RUN_AT_INVALID")
    return {
      texto: `La hora tiene que ser entre un minuto y ${TAREA_MAX_DIAS} días desde ahora`,
      nivel: "warning",
      releer: false,
    };
  const detalle =
    typeof r?.data?.message === "string"
      ? r.data.message
      : (e as { message?: string })?.message ?? String(e);
  return {
    texto: `No se pudo ${
      accion === "cancelar" ? "cancelar la tarea" : "cambiar la hora"
    }: ${detalle}`,
    nivel: "error",
    releer: false,
  };
}

// ── La franja de la conversación ───────────────────────────────────────────

/** Las tareas de la conversación abierta, como las devolvió la última lectura. */
export interface EstadoDeLaFranja {
  conversationId: string | null;
  tareas: TaskView[];
}

export const FRANJA_VACIA: EstadoDeLaFranja = {
  conversationId: null,
  tareas: [],
};

export type AccionDeLaFranja =
  /** Se abrió otra conversación: lo de la anterior no se muestra ni un instante. */
  | { tipo: "abrir"; conversationId: string | null }
  /** Volvió `GET …/agent-tasks`: reemplaza todo. La de otra conversación, que llegó tarde, se tira. */
  | { tipo: "leidas"; conversationId: string; tareas: TaskView[] }
  /** Volvió un cancelar o un cambiar la hora: esa tarea, en su lugar. */
  | { tipo: "cambiada"; tarea: TaskView };

export function reducirFranja(
  estado: EstadoDeLaFranja,
  accion: AccionDeLaFranja,
): EstadoDeLaFranja {
  switch (accion.tipo) {
    case "abrir":
      return { conversationId: accion.conversationId, tareas: [] };
    case "leidas":
      if (accion.conversationId !== estado.conversationId) return estado;
      return { ...estado, tareas: accion.tareas };
    case "cambiada": {
      if (accion.tarea.conversationId !== estado.conversationId) return estado;
      const esta = estado.tareas.some((t) => t.taskId === accion.tarea.taskId);
      return {
        ...estado,
        tareas: esta
          ? estado.tareas.map((t) =>
              t.taskId === accion.tarea.taskId ? accion.tarea : t,
            )
          : [...estado.tareas, accion.tarea],
      };
    }
    default:
      return estado;
  }
}

/** Lo que va en la franja: lo que todavía no terminó, de la más próxima a la más lejana. */
export function tareasDeLaFranja(tareas: TaskView[], ahora: Date): TaskView[] {
  return tareas
    .filter((t) => {
      const s = estadoDeTarea(t, ahora);
      return s === "pending" || s === "late" || s === "running";
    })
    .sort((a, b) => a.runAt.localeCompare(b.runAt));
}

/** Lo que muestra la tarjeta de una nota, desde la lista. */
export function comoTareaDeNota(t: TaskView): NoteTask {
  return {
    taskId: t.taskId,
    runAt: t.runAt,
    status: t.status,
    cancelledByName: t.cancelledByName,
    cancelledAt: t.cancelledAt,
  };
}

/** Las tareas de la lista, por la nota del asistente que las confirma. */
export function tareasPorNota(tareas: TaskView[]): Map<string, TaskView[]> {
  const m = new Map<string, TaskView[]>();
  for (const t of tareas) {
    if (!t.confirmationNoteId) continue;
    m.set(t.confirmationNoteId, [...(m.get(t.confirmationNoteId) ?? []), t]);
  }
  return m;
}

/**
 * Las tarjetas de una nota. La lista es lo último que se leyó —se relee con cada `agent:tasks`— y
 * gana; la tarea que trajo el timeline queda si la lista no la tiene: una terminada hace rato ya no
 * está entre las últimas 10, o la nota llegó antes que la relectura.
 */
export function tareasDeLaNota(
  propia: NoteTask | null | undefined,
  deLaLista: TaskView[] | undefined,
): NoteTask[] {
  const frescas = (deLaLista ?? []).map(comoTareaDeNota);
  const todas =
    propia && !frescas.some((t) => t.taskId === propia.taskId)
      ? [propia, ...frescas]
      : frescas;
  return todas.sort((a, b) => a.runAt.localeCompare(b.runAt));
}

/**
 * A qué fila del hilo lleva una tarea de la franja: la de su nota de confirmación o, si no está, la
 * del pedido. Si la nota está anidada en un mensaje, la burbuja. `null`: no está cargada.
 */
export function destinoDeLaTarea(
  rows: TimelineRow[],
  t: Pick<TaskView, "confirmationNoteId" | "requestNoteId">,
): string | null {
  for (const noteId of [t.confirmationNoteId, t.requestNoteId]) {
    if (!noteId) continue;
    const fila = rows.find(
      (r) =>
        (r.kind === "note" && r.id === noteId) ||
        (r.kind === "message" &&
          (r.payload.notes ?? []).some((n) => n.noteId === noteId)),
    );
    if (fila) return fila.id;
  }
  return null;
}
